import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type {
	MetaAdsCreateCampaignTemplateDto,
	MetaAdsSaveAsTemplateDto,
	MetaAdsUpdateCampaignTemplateDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useCampaignTemplates = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.CampaignTemplates(company.id),
		queryFn: () => Api.GetMetaAdsCampaignTemplates().then((r) => r.data),
	});
};

export const useCampaignTemplateActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const createTemplate = useMutation({
		mutationFn: (dto: MetaAdsCreateCampaignTemplateDto) =>
			Api.CreateMetaAdsCampaignTemplate(dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.CampaignTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Campaign template created." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to create campaign template.",
			});
		},
	});

	const updateTemplate = useMutation({
		mutationFn: ({
			id,
			dto,
		}: {
			id: number;
			dto: MetaAdsUpdateCampaignTemplateDto;
		}) => Api.UpdateMetaAdsCampaignTemplate(id, dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.CampaignTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Campaign template updated." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update campaign template.",
			});
		},
	});

	const deleteTemplate = useMutation({
		mutationFn: (id: number) =>
			Api.DeleteMetaAdsCampaignTemplate(id).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.CampaignTemplates(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Campaign template deleted." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to delete campaign template.",
			});
		},
	});

	const saveAsTemplate = useMutation({
		mutationFn: ({
			draftId,
			dto,
		}: {
			draftId: string;
			dto: MetaAdsSaveAsTemplateDto;
		}) => Api.SaveMetaAdsDraftAsTemplate(draftId, dto).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.CampaignTemplates(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "Draft saved as campaign template.",
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to save draft as template.",
			});
		},
	});

	return { createTemplate, updateTemplate, deleteTemplate, saveAsTemplate };
};
